<template>
  <div
    class="field-dropdown-wrapper field-multiselect-dropdown-wrapper"
    :class="{ 'tiny-field': field.isSmallField, error: field.hasError }"
    v-click-outside="onClickOutsideDropdown"
  >
    <!-- Trigger / Display -->
    <div
      class="dropdown-info-wrapper"
      :disabled="field.disabled === true"
      @click="toggleDropdown"
      :aria-expanded="showOptions ? 'true' : 'false'"
      role="button"
      tabindex="0"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
    >
      <label
        v-if="field.value && field.value.length"
        class="field-placeholder field-placeholder-value"
      >
        <span
          v-for="val in field.value"
          :key="val"
          class="selected-dropdown-item"
          >{{ val }}</span
        >
      </label>
      <label v-else class="field-placeholder">{{ field.placeholder }}</label>
      <unicon class="field-icon" name="angle-down"></unicon>
    </div>

    <!-- Options -->
    <div class="dropdown-list-wrapper" :class="{ show: showOptions }">
      <ul class="dropdown-list">
        <li
          v-for="(option, idx) in field.fields"
          :key="option"
          class="dropdown-item"
        >
          <div class="checkbox-field">
            <input
              type="checkbox"
              :id="idFor(idx)"
              :value="option"
              v-model="field.value"
              :disabled="field.disabled"
              @change="fieldChanged"
            />
            <span class="checkmark"></span>
          </div>
          <label class="checkbox-label" :for="idFor(idx)">{{ option }}</label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
// Importing directives
import vClickOutside from "v-click-outside";

export default {
  name: "MultiSelectDropdown",
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showOptions: false,
      idPrefix: "ms-" + Math.random().toString(36).slice(2),
    };
  },
  methods: {
    toggleDropdown() {
      if (this.field.disabled) return;
      this.showOptions = !this.showOptions;
    },
    onClickOutsideDropdown() {
      this.showOptions = false;
    },
    fieldChanged() {
      this.$emit("fieldChanged", this.field.value);
    },
    idFor(index) {
      return `${this.idPrefix}-${index}`;
    },
  },
};
</script>

<style scoped lang="scss">
/* Container */
.field-dropdown-wrapper {
  position: relative;
  width: 100%;
}
.field-multiselect-dropdown-wrapper {
  position: relative;
  width: 100%;
}

/* Trigger / Display */
.dropdown-info-wrapper {
  position: relative;
  cursor: pointer;
  transition: all $transitionSpeed;
  display: block;
  font-size: 1rem;
  font-weight: 500;
  outline: none;
  background-color: $whiteColor;
  color: $inputTextColor;
  width: calc(100% - 2.5rem);
  padding: 0.75rem 1.15rem;
  border: 1px solid $inputBorderColor;
  border-radius: 0.5rem;
  -webkit-appearance: none;

  &::placeholder {
    font-size: $smallFontSize;
    font-weight: 400;
    color: lighten($blackColor, 50%);
  }
}

.field-placeholder {
  display: block;
  cursor: pointer;
  font-size: $smallFontSize;
  line-height: 1.5;
  font-weight: 400;
  color: lighten($greyColor, 25%);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 90%;
}

.field-placeholder-value {
  color: $blackColor !important;
  font-weight: $mediumFontWeight !important;
}

/* Caret icon */
.field-icon {
  @include vertical-center-align;
  margin-top: 0.1rem;
  right: 0.5rem;

  ::v-deep svg {
    fill: $lightGreyColor;
    transition: transform $transitionSpeed;
  }
}
/* Optional: rotate caret when wrapper has .open */
.field-multiselect-dropdown-wrapper.open .field-icon {
  transform: rotate(180deg);
}

.dropdown-info-wrapper:hover {
  border-color: lighten($blackColor, 15%) !important;
}

.error .dropdown-info-wrapper {
  border-color: $redColor !important;
}

/* Panel */
.dropdown-list-wrapper {
  position: absolute;
  display: none;
  left: 0;
  top: 100%;
  width: 100%;
  z-index: 10;

  &.show {
    display: block;
  }
}

/* Options list (scrollable) */
.dropdown-list {
  background-color: $whiteColor;
  box-shadow: 0 0 0.7rem -0.1rem rgba(0, 0, 0, 0.05);
  border: 1px solid $inputBorderColor;
  border-radius: 0.5rem;
  padding: 0.5rem 0;
  margin: 0;

  /* Max height + scroll */
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* Scrollbar (webkit) */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: lighten($blackColor, 70%);
    border-radius: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Scrollbar (Firefox) */
  scrollbar-width: thin;
  scrollbar-color: lighten($blackColor, 70%) transparent;
}

/* Option row */
.dropdown-item {
  position: relative;
  cursor: pointer;
  background-color: transparent;

  .checkbox-field {
    @include vertical-center-align;
    left: 0.5rem;

    /* Hide native checkbox but keep accessible */
    input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    /* Custom box */
    .checkmark {
      position: relative;
      display: inline-block;
      width: 18px;
      height: 18px;
      border: 1.5px solid lighten($blackColor, 65%);
      border-radius: 4px;
      background: $whiteColor;
      transition: background-color $transitionSpeed,
        border-color $transitionSpeed, box-shadow $transitionSpeed;
    }

    /* Tick */
    .checkmark::after {
      content: "";
      position: absolute;
      display: none;
      left: 5px;
      top: 3px;
      width: 4px;
      height: 8px;
      border: solid $whiteColor;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    /* Checked state */
    input:checked + .checkmark {
      background: $greenColor;
      border-color: $greenColor;
      box-shadow: 0 0 0 2px rgba($greenColor, 0.12);
    }
    input:checked + .checkmark::after {
      display: block;
    }

    /* Focus ring */
    input:focus + .checkmark {
      box-shadow: 0 0 0 2px rgba($greenColor, 0.25);
    }
  }

  .checkbox-label {
    display: block;
    cursor: pointer;
    color: $greyColor;
    font-size: $smallFontSize;
    padding: 0.75rem 0.75rem 0.75rem 2.5rem;
    user-select: none;
  }

  &:hover {
    background-color: darken($whiteColor, 5%);
    .checkbox-label {
      color: $blackColor;
    }
  }

  /* Persist highlight when selected */
  &.selected {
    background-color: lighten($greenColor, 42%);
    .checkbox-label {
      color: $blackColor;
      font-weight: $mediumFontWeight;
    }
  }
}

/* Selected values renderer (in trigger) */
.selected-dropdown-item {
  display: inline-block;
  font-size: $smallFontSize;
  margin-right: 0.35rem;

  &::after {
    content: ",";
  }
  &:last-child::after {
    content: "";
  }
}

/* Disabled state */
.dropdown-info-wrapper[disabled] {
  background: rgba(239, 239, 239, 0.3);
  cursor: not-allowed;

  .field-icon {
    display: none;
  }

  .field-placeholder {
    width: auto;
  }
}

/* Tiny variant */
.tiny-field {
  .dropdown-info-wrapper {
    padding: 0.5rem 0.75rem;
  }
  .field-placeholder {
    font-size: $smallerFontSize;
  }
}
</style>
